import { useState, useEffect, useMemo } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Sliders,
  Layers,
  CheckCircle2,
  XCircle,
  HelpCircle,
  ShieldCheck,
  Compass,
  UserX,
  TrendingDown,
  Building2,
  Boxes,
  Scale,
  Tag,
  Shirt,
  Palette,
  MessageSquareWarning,
  Handshake,
} from "lucide-react";
import type { MasterLookupRecord } from "@/types";

export const MASTER_CATEGORIES = [
  { key: "LEAD_SOURCE", label: "Lead Sources", icon: Compass, description: "Origin of inbound & outbound leads" },
  { key: "LEAD_DISQUALIFY_REASON", label: "Disqualify Reasons", icon: UserX, description: "Why leads are dropped" },
  { key: "DEAL_LOST_REASON", label: "Deal Lost Reasons", icon: TrendingDown, description: "Reasons pipeline deals are marked lost" },
  { key: "INDUSTRY", label: "Buyer Types", icon: Building2, description: "Kind of textile business a buyer runs (exporter, trader, brand...)" },
  { key: "PRODUCT_CATEGORY", label: "Product Categories", icon: Boxes, description: "Fibre / material family like Cotton, Polyester, Blends" },
  { key: "PRODUCT_UNIT", label: "Units of Measure", icon: Scale, description: "Units like MTR, YDS, KG, PCS, ROLL" },
  { key: "END_USE", label: "End Uses", icon: Shirt, description: "What the buyer makes: shirting, suiting, home furnishing..." },
  { key: "SAMPLE_TYPE", label: "Sample Types", icon: Palette, description: "Swatch, lab dip, strike-off, PP sample..." },
  { key: "COMPLAINT_TYPE", label: "Complaint Types", icon: MessageSquareWarning, description: "Shade variation, GSM, shrinkage, short quantity..." },
  { key: "RESOLUTION_TYPE", label: "Resolutions", icon: Handshake, description: "Credit note, replacement, discount, refund..." },
];

export function MasterLookupPage() {
  const [items, setItems] = useState<MasterLookupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("LEAD_SOURCE");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<MasterLookupRecord | null>(null);
  const [deleteItem, setDeleteItem] = useState<MasterLookupRecord | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    category: "LEAD_SOURCE",
    name: "",
    code: "",
    color_code: "#3b82f6",
    description: "",
    sort_order: 0,
    status: true,
  });

  const { success, error } = useToast();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/MasterLookup/List");
      setItems(unwrapArray<MasterLookupRecord>(res));
    } catch (err: any) {
      error("Fetch Failed", err.message || "Failed to load master lookups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openAdd = () => {
    setEditItem(null);
    setForm({
      category: activeCategory === "ALL" ? "LEAD_SOURCE" : activeCategory,
      name: "",
      code: "",
      color_code: "#3b82f6",
      description: "",
      sort_order: (filteredItems.length + 1),
      status: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (item: MasterLookupRecord) => {
    setEditItem(item);
    setForm({
      category: item.category,
      name: item.name,
      code: item.code,
      color_code: item.color_code || "#3b82f6",
      description: item.description || "",
      sort_order: item.sort_order,
      status: item.status,
    });
    setDialogOpen(true);
  };

  const handleNameChange = (val: string) => {
    setForm((prev) => ({
      ...prev,
      name: val,
      code: editItem ? prev.code : val.toUpperCase().replace(/[^A-Z0-9]/g, "_").slice(0, 32),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      error("Validation Error", "Name and code are required");
      return;
    }

    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/api/MasterLookup/Update/${editItem.id}`, form);
        success("Updated", `Master lookup "${form.name}" updated successfully`);
      } else {
        await api.post("/api/MasterLookup/Insert", form);
        success("Created", `Master lookup "${form.name}" created successfully`);
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err: any) {
      error("Save Error", err.message || "Failed to save master lookup");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    try {
      await api.delete(`/api/MasterLookup/Delete/${deleteItem.id}`);
      success("Deleted", `"${deleteItem.name}" deleted successfully`);
      setDeleteItem(null);
      fetchItems();
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete lookup item");
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchCat = activeCategory === "ALL" || item.category === activeCategory;
      const matchStatus =
        statusFilter === "ALL"
          ? true
          : statusFilter === "ACTIVE"
          ? item.status
          : !item.status;
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q ||
        item.name.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q) ||
        (item.description && item.description.toLowerCase().includes(q));

      return matchCat && matchStatus && matchSearch;
    });
  }, [items, activeCategory, statusFilter, search]);

  const activeCategoryMeta = MASTER_CATEGORIES.find((c) => c.key === activeCategory);

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Sliders className="size-6 text-primary" />
            <span>Master Data Lookups</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Manage dropdown choices, lead sources, deal lost reasons, industries, and product units
          </p>
        </div>

        <Button onClick={openAdd} className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus className="size-4" />
          <span>Add Option</span>
        </Button>
      </div>

      {/* Category Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 border-b">
        {MASTER_CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const count = items.filter((i) => i.category === cat.key).length;
          const isActive = activeCategory === cat.key;
          return (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-t-lg transition-all border-b-2 whitespace-nowrap ${
                isActive
                  ? "border-primary text-primary bg-primary/5"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              <Icon className="size-3.5" />
              <span>{cat.label}</span>
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                  isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
        <button
          onClick={() => setActiveCategory("ALL")}
          className={`flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-t-lg transition-all border-b-2 whitespace-nowrap ${
            activeCategory === "ALL"
              ? "border-primary text-primary bg-primary/5"
              : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
          }`}
        >
          <Layers className="size-3.5" />
          <span>All Masters</span>
          <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono bg-muted text-muted-foreground">
            {items.length}
          </span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input
            placeholder={`Search ${activeCategoryMeta?.label || "masters"}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-9 text-xs"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs text-muted-foreground font-medium">Status:</span>
          {(["ALL", "ACTIVE", "INACTIVE"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 text-xs rounded-md font-medium border transition-colors ${
                statusFilter === s
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background hover:bg-muted text-muted-foreground"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Main Table */}
      <div className="rounded-xl border bg-card shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-xs text-muted-foreground">Loading master lookups...</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-12 text-center space-y-2">
            <HelpCircle className="size-8 text-muted-foreground/50 mx-auto" />
            <p className="text-sm font-semibold text-foreground">No options found</p>
            <p className="text-xs text-muted-foreground">
              {search
                ? "Try adjusting your search query or status filter."
                : `No master records exist in this category yet. Click "Add Option" to create one.`}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="w-12 text-center text-xs">#</TableHead>
                <TableHead className="text-xs">Option Name</TableHead>
                <TableHead className="text-xs">Code / Identifier</TableHead>
                {activeCategory === "ALL" && <TableHead className="text-xs">Category</TableHead>}
                <TableHead className="text-xs">Description</TableHead>
                <TableHead className="text-xs text-center w-24">Type</TableHead>
                <TableHead className="text-xs text-center w-20">Status</TableHead>
                <TableHead className="text-xs text-right w-24 pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item, idx) => (
                <TableRow key={item.id} className="hover:bg-muted/30 transition-colors">
                  <TableCell className="text-center text-xs text-muted-foreground font-mono">
                    {item.sort_order || idx + 1}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {item.color_code && (
                        <span
                          className="size-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: item.color_code }}
                        />
                      )}
                      <span className="font-semibold text-xs text-foreground">{item.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-[11px] font-medium bg-muted/30">
                      {item.code}
                    </Badge>
                  </TableCell>
                  {activeCategory === "ALL" && (
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px]">
                        {item.category}
                      </Badge>
                    </TableCell>
                  )}
                  <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                    {item.description || "—"}
                  </TableCell>
                  <TableCell className="text-center">
                    {item.is_system ? (
                      <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-[10px] gap-1 font-semibold">
                        <ShieldCheck className="size-3" /> System
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground font-normal gap-1 inline-flex items-center">
                        <Tag className="size-2.5" /> Custom
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {item.status ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                        <CheckCircle2 className="size-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
                        <XCircle className="size-3" /> Inactive
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right pr-4">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(item)}
                        className="size-7 p-0 text-muted-foreground hover:text-foreground"
                        title="Edit option"
                      >
                        <Edit2 className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteItem(item)}
                        disabled={item.is_system}
                        className={`size-7 p-0 ${
                          item.is_system
                            ? "opacity-30 cursor-not-allowed"
                            : "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        }`}
                        title={item.is_system ? "System defaults cannot be deleted" : "Delete option"}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl bg-card border-border text-foreground">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-bold flex items-center gap-2.5">
                <Sliders className="size-5 text-primary" />
                <span>{editItem ? "Edit Master Option" : "Add Master Option"}</span>
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
                Configure standardized dropdown choice for CRM modules.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 sm:space-y-4.5 py-4">
              <div className="space-y-2">
                <Label htmlFor="category">
                  Category *
                </Label>
                <select
                  id="category"
                  value={form.category}
                  disabled={!!editItem}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  className="w-full h-10 sm:h-11 rounded-lg border border-input bg-background px-3 text-sm sm:text-[15px] focus:outline-hidden focus:ring-2 focus:ring-ring"
                >
                  {MASTER_CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label} ({c.key})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">
                  Display Label / Name *
                </Label>
                <Input
                  id="name"
                  placeholder="e.g. Inbound Website, Price Too High"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">
                  Code / Identifier *
                </Label>
                <Input
                  id="code"
                  placeholder="e.g. WEBSITE_INBOUND"
                  value={form.code}
                  disabled={editItem?.is_system}
                  onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                  required
                  className="font-mono uppercase"
                />
                <span className="text-xs text-muted-foreground block">
                  Unique programmatic key used for APIs and reports
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="color_code">
                    Color Badge (Optional)
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      id="color_code"
                      value={form.color_code || "#3b82f6"}
                      onChange={(e) => setForm((p) => ({ ...p, color_code: e.target.value }))}
                      className="size-10 sm:size-11 rounded-lg border border-input cursor-pointer p-0.5 bg-background shrink-0"
                    />
                    <Input
                      value={form.color_code || ""}
                      placeholder="#3b82f6"
                      onChange={(e) => setForm((p) => ({ ...p, color_code: e.target.value }))}
                      className="font-mono flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sort_order">
                    Sort Order
                  </Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  Description / Notes
                </Label>
                <Input
                  id="description"
                  placeholder="Additional context about this option"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-2.5 pt-1">
                <input
                  type="checkbox"
                  id="status"
                  checked={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked }))}
                  className="rounded border-input text-primary focus:ring-primary size-4"
                />
                <Label htmlFor="status" className="font-medium cursor-pointer">
                  Active (visible in dropdowns across CRM)
                </Label>
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : editItem ? "Update Option" : "Create Option"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg text-red-600 dark:text-red-400">Confirm Delete</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Are you sure you want to delete <strong className="text-foreground">{deleteItem?.name}</strong>? Existing historical records
              will keep their string code, but this option will no longer appear in dropdowns.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setDeleteItem(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? "Deleting..." : "Delete Option"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
